'use client';

import { useId } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useScrollArrows } from '@/hooks/useScrollArrows';

export interface FilterOption<T> {
  label: string;
  value: T;
}

type ChipVariant = 'underline' | 'pill' | 'tier2';

// Shared, variant-independent classes applied to every chip button.
const CHIP_BASE =
  'shrink-0 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1';

// Per-variant class strings. `underline` and `pill` are the historical looks and
// must stay identical (other filter surfaces depend on them).
// `tier2` is the sage section-tab pill that matches the My Groups inner tabs.
//
// One deliberate exception: `pill` carries `whitespace-nowrap`, matching `tier2`.
// The My Groups group selector used to hand-roll this look WITH that class, and
// it renders on mobile inside a `flex-nowrap` scrolling row. Dropping it risked
// group names wrapping inside their chip on mobile - a regression on a surface
// that works today. It is a visual no-op for the other pill consumers, whose
// chips already sit in a nowrap row. Do not "restore" it by removing this.
//
// FOCUS-RING HEADROOM (`pt-1` / `px-1`): the chips draw their focus ring OUTSIDE
// their own box - `ring-2` + `ring-offset-1` = 3px of bleed on every side. Setting
// `overflow-x: auto` forces the cross axis to a non-visible value too, so this
// element clips vertically as well as horizontally. Without padding, a selected or
// keyboard-focused chip gets its ring sliced off at the top (and the first/last
// chip loses it sideways). The 4px of padding is the room the ring needs; the
// negative margin on the wrapper (see ChipRow) cancels the horizontal inset so
// chips still line up flush with the rest of the page.
const VARIANT_CLASSES: Record<ChipVariant, { container: string; active: string; inactive: string }> = {
  underline: {
    container: 'flex gap-2 pt-1 pb-2 px-1 overflow-x-auto md:overflow-visible md:flex-wrap md:pb-0 scrollbar-thin',
    active: 'px-4 py-1.5 rounded border-b-2 border-foreground text-foreground',
    inactive: 'px-4 py-1.5 rounded text-foreground-muted hover:text-foreground',
  },
  pill: {
    container: 'flex gap-2 pt-1 pb-2 px-1 overflow-x-auto flex-nowrap scrollbar-thin',
    active: 'px-5 py-2 rounded-xl border border-primary bg-primary text-background whitespace-nowrap',
    inactive: 'px-5 py-2 rounded-xl border border-border bg-background text-foreground hover:border-primary hover:text-primary whitespace-nowrap',
  },
  tier2: {
    container: 'flex gap-2 pt-1 pb-2 px-1 overflow-x-auto flex-nowrap scrollbar-thin',
    active: 'px-4 py-1.5 rounded-full border border-filter-bg bg-tab-active-bg text-black whitespace-nowrap',
    inactive: 'px-4 py-1.5 rounded-full border border-border bg-white text-foreground-muted hover:border-primary hover:text-foreground whitespace-nowrap',
  },
};

const ARROW_BASE =
  'hidden md:flex shrink-0 items-center justify-center h-8 w-8 rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1';
const ARROW_ENABLED = 'border-border bg-background text-foreground hover:border-primary hover:text-primary';
const ARROW_BLOCKED = 'border-border bg-background text-foreground opacity-40 cursor-default';

interface ScrollArrowProps {
  direction: 'prev' | 'next';
  label: string;
  /** False when the row is already at that edge. The button stays focusable; see below. */
  enabled: boolean;
  onScroll: () => void;
  controls: string;
  testId: string;
}

/**
 * A prev/next control for an overflowing chip row.
 *
 * Uses `aria-disabled` rather than the native `disabled` attribute on purpose.
 * A natively disabled button leaves the tab order, so a keyboard user pressing
 * Enter on "next" until they reach the end would have focus destroyed and fall
 * back to <body>. `aria-disabled` keeps focus where the user put it and is still
 * announced as unavailable. The click handler no-ops at the edge (the scroll hook
 * clamps), so it cannot over-scroll.
 *
 * Hidden below 768px (`hidden md:flex`): mobile reaches the same chips by swiping,
 * and `display: none` keeps these out of the mobile layout AND its tab order.
 */
function ScrollArrow({ direction, label, enabled, onScroll, controls, testId }: ScrollArrowProps) {
  const Icon = direction === 'prev' ? ChevronLeft : ChevronRight;

  return (
    <button
      type="button"
      onClick={onScroll}
      aria-label={label}
      aria-controls={controls}
      aria-disabled={!enabled}
      data-testid={testId}
      className={`${ARROW_BASE} ${enabled ? ARROW_ENABLED : ARROW_BLOCKED}`}
    >
      <Icon size={16} aria-hidden="true" />
    </button>
  );
}

/**
 * Resolve a possibly out-of-union variant to a real one.
 *
 * Kept as a single source of truth so the class lookup and the arrow decision can
 * never disagree about which variant is in play.
 *
 * `Object.hasOwn`, not `in`: `in` walks the prototype chain, so it answers true for
 * every inherited key. A loosely-typed caller passing "toString" or "constructor"
 * would be waved through, and the lookup would then hand back a function instead of
 * a class bundle - yielding `undefined` classes on every chip. An own-property check
 * is what this function's contract actually promises.
 */
function resolveVariant(variant: ChipVariant): ChipVariant {
  return Object.hasOwn(VARIANT_CLASSES, variant) ? variant : 'underline';
}

/**
 * `underline` is the only variant that wraps onto multiple rows on desktop
 * (`md:flex-wrap`), so it has no hidden content and needs no arrows. `pill` and
 * `tier2` stay on one scrolling line, which is what makes them unreachable by
 * mouse without the arrows.
 */
function variantScrolls(variant: ChipVariant): boolean {
  return resolveVariant(variant) !== 'underline';
}

interface ChipRowProps {
  className: string;
  containerClasses: string;
  ariaLabel: string;
  testIdPrefix: string;
  scrollPrevLabel: string;
  scrollNextLabel: string;
  /** False for variants that wrap instead of scrolling; they never show arrows. */
  withArrows: boolean;
  children: React.ReactNode;
}

/**
 * The scrolling row plus its two arrows.
 *
 * `role="group"` stays on the SCROLLING element, not this wrapper, and the arrows
 * render outside it. The group is the set of chips; the arrows are controls *for*
 * that group, wired to it with `aria-controls`. (Consumers also count the buttons
 * inside the group, so putting arrows in it would corrupt that count.)
 *
 * Arrows only mount when the row actually overflows. Rendering them always would
 * add two permanently-dead controls to the accessibility tree of every filter row
 * in the app, almost none of which overflow.
 */
function ChipRow({
  className,
  containerClasses,
  ariaLabel,
  testIdPrefix,
  scrollPrevLabel,
  scrollNextLabel,
  withArrows,
  children,
}: ChipRowProps) {
  const { scrollRef, isOverflowing, canScrollLeft, canScrollRight, scrollPrev, scrollNext } =
    useScrollArrows<HTMLDivElement>();
  const scrollerId = useId();
  const showArrows = withArrows && isOverflowing;

  return (
    // `min-w-0` on the wrapper as well as the scroller: some callers place this row
    // inside a flex parent (e.g. `flex justify-center`), where the default
    // min-width:auto would stop it shrinking and push the page wide instead of
    // letting the row scroll within its own box.
    <div className={`flex items-center gap-2 min-w-0 ${className}`} data-testid={`${testIdPrefix}-root`}>
      {showArrows && (
        <ScrollArrow
          direction="prev"
          label={scrollPrevLabel}
          enabled={canScrollLeft}
          onScroll={scrollPrev}
          controls={scrollerId}
          testId={`${testIdPrefix}-scroll-prev`}
        />
      )}

      <div
        ref={scrollRef}
        id={scrollerId}
        role="group"
        aria-label={ariaLabel}
        // `-mx-1` cancels the container's `px-1` focus-ring gutter, so the chips
        // still sit flush with the page while the ring keeps its room to paint.
        className={`${containerClasses} flex-1 min-w-0 -mx-1`}
        data-testid={`${testIdPrefix}-scroller`}
      >
        {children}
      </div>

      {showArrows && (
        <ScrollArrow
          direction="next"
          label={scrollNextLabel}
          enabled={canScrollRight}
          onScroll={scrollNext}
          controls={scrollerId}
          testId={`${testIdPrefix}-scroll-next`}
        />
      )}
    </div>
  );
}

interface ChipRowConfigProps {
  className?: string;
  ariaLabel?: string;
  variant?: ChipVariant;
  /**
   * Namespaces every data-testid this component emits. Required whenever two chip
   * rows can render on the same screen (e.g. the My Groups group selector and the
   * forum-category tabs), so tests never have to disambiguate by array index.
   */
  testIdPrefix?: string;
  /** Accessible name for the "scroll backwards" arrow. Pass a translated string. */
  scrollPrevLabel?: string;
  /** Accessible name for the "scroll forwards" arrow. Pass a translated string. */
  scrollNextLabel?: string;
}

// English defaults, mirroring `ariaLabel`. This component must NOT call useLanguage():
// it throws without a LanguageProvider, and FilterChips is rendered bare in tests and
// is a presentational primitive. Callers own translation and pass it down.
const DEFAULT_PREV_LABEL = 'Scroll filters left';
const DEFAULT_NEXT_LABEL = 'Scroll filters right';

interface FilterChipsProps<T> extends ChipRowConfigProps {
  options: FilterOption<T>[];
  selected: T;
  onChange: (value: T) => void;
}

export function FilterChips<T>({
  options,
  selected,
  onChange,
  className = '',
  ariaLabel = 'Filter options',
  variant = 'underline',
  testIdPrefix = 'filter-chips',
  scrollPrevLabel = DEFAULT_PREV_LABEL,
  scrollNextLabel = DEFAULT_NEXT_LABEL,
}: FilterChipsProps<T>) {
  const styles = VARIANT_CLASSES[resolveVariant(variant)];

  return (
    <ChipRow
      className={className}
      containerClasses={styles.container}
      ariaLabel={ariaLabel}
      testIdPrefix={testIdPrefix}
      scrollPrevLabel={scrollPrevLabel}
      scrollNextLabel={scrollNextLabel}
      withArrows={variantScrolls(variant)}
    >
      {options.map((option, index) => {
        const isSelected = option.value === selected;
        return (
          <button
            key={index}
            onClick={() => onChange(option.value)}
            aria-pressed={isSelected}
            data-testid={`${testIdPrefix}-${String(option.value)}`}
            className={`${CHIP_BASE} ${isSelected ? styles.active : styles.inactive}`}
          >
            {option.label}
          </button>
        );
      })}
    </ChipRow>
  );
}

interface MultiFilterChipsProps<T> extends ChipRowConfigProps {
  options: FilterOption<T>[];
  selected: T[];
  onChange: (values: T[]) => void;
}

export function MultiFilterChips<T>({
  options,
  selected,
  onChange,
  className = '',
  ariaLabel = 'Filter options',
  variant = 'underline',
  testIdPrefix = 'filter-chips',
  scrollPrevLabel = DEFAULT_PREV_LABEL,
  scrollNextLabel = DEFAULT_NEXT_LABEL,
}: MultiFilterChipsProps<T>) {
  const handleClick = (value: T) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const styles = VARIANT_CLASSES[resolveVariant(variant)];

  return (
    <ChipRow
      className={className}
      containerClasses={styles.container}
      ariaLabel={ariaLabel}
      testIdPrefix={testIdPrefix}
      scrollPrevLabel={scrollPrevLabel}
      scrollNextLabel={scrollNextLabel}
      withArrows={variantScrolls(variant)}
    >
      {options.map((option, index) => {
        const isSelected = selected.includes(option.value);
        return (
          <button
            key={index}
            onClick={() => handleClick(option.value)}
            aria-pressed={isSelected}
            className={`${CHIP_BASE} ${isSelected ? styles.active : styles.inactive}`}
          >
            {option.label}
          </button>
        );
      })}
    </ChipRow>
  );
}
